import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Clock, Utensils, Armchair, ArrowRight, BookOpen, ShieldCheck, AlertCircle, Leaf, Activity } from 'lucide-react';
// Brand Colors
const COLORS = {
  teal: '#3D6B6B',
  cream: '#F5F0E8',
  coral: '#E8846B',
  white: '#FFFFFF',
  text: '#2D3748'
};
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function App() {
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen font-sans text-gray-800 selection:bg-[#E8846B] selection:text-white" style={{
    backgroundColor: COLORS.cream
  }} data-id="element-0">
      {/* HERO SECTION */}
      <header className="relative overflow-hidden pt-8 pb-12 sm:pt-12 sm:pb-16 lg:pt-24 lg:pb-32 px-4 sm:px-6" data-id="element-1">
        <div className="max-w-6xl mx-auto" data-id="element-2">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-3">
            <motion.div initial="hidden" animate="visible" variants={fadeIn} className="order-2 lg:order-1 text-center lg:text-left" data-id="element-4">
              <span className="inline-block py-1 px-3 rounded-full text-xs sm:text-sm font-bold tracking-wide mb-4 sm:mb-6 text-white" style={{
              backgroundColor: COLORS.teal
            }} data-id="element-5">
                PARA MUJERES 65+
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight mb-4 sm:mb-6 text-[#3D6B6B]" data-id="element-6">
                12 minutos después de comer: tu nuevo "anti-hinchazón"
              </h1>
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-700 mb-6 sm:mb-8 leading-relaxed" data-id="element-7">
                El protocolo suave de 21 días para sentir menos hinchazón sin
                dietas raras, sin ejercicios en el piso y sin ingredientes
                extraños.
              </p>
              <button onClick={scrollToOffer} className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-lg sm:text-xl font-bold text-white shadow-lg active:scale-95 sm:hover:shadow-xl sm:hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2" style={{
              backgroundColor: COLORS.coral
            }} data-id="element-8">
                Quiero RESET 21 <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-9" />
              </button>
              <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500 flex items-center justify-center lg:justify-start gap-2" data-id="element-10">
                <ShieldCheck className="w-4 h-4" data-id="element-11" /> Compra segura y garantizada
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="order-1 lg:order-2 relative max-w-xs sm:max-w-sm mx-auto lg:max-w-none" data-id="element-12">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl" data-id="element-13">
                <img src="https://cdn.magicpatterns.com/uploads/bhNzPPaBTBr2BY1JxcdAt6/LIBRO_NUEVO.png" alt="RESET 21 Libro Digital" className="w-full h-auto object-cover" data-id="element-14" />
              </div>
              {/* Decorative blob - hidden on mobile for performance */}
              <div className="hidden sm:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[#3D6B6B]/10 rounded-full blur-3xl -z-10" data-id="element-15" />
            </motion.div>
          </div>
        </div>
      </header>

      {/* OPENING LEAD - Emotional Hook */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-white" data-id="element-16">
        <div className="max-w-3xl mx-auto text-base sm:text-lg lg:text-xl leading-relaxed text-gray-700 space-y-6 sm:space-y-8" data-id="element-17">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={fadeIn} data-id="element-18">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#3D6B6B] mb-6 sm:mb-8" data-id="element-19">
              Hay un tipo de hinchazón que no se siente como "algo leve".
            </h2>
            <p data-id="element-20">
              Se siente como: <strong data-id="element-21">ropa apretada</strong>, abdomen tenso,
              pesadez… y esa idea molesta de "¿en serio mi cuerpo ahora es así?"
            </p>
            <p data-id="element-22">
              Y lo peor no es solo el abdomen. Es la parte mental: empezar a
              evitar comidas, evitar salidas, evitar fotos… por miedo a cómo te
              vas a sentir <strong data-id="element-23">después</strong>.
            </p>
            <div className="p-4 sm:p-6 bg-[#F5F0E8] rounded-xl border-l-4 border-[#3D6B6B] my-6 sm:my-8" data-id="element-24">
              <p className="font-medium italic text-[#3D6B6B] text-base sm:text-lg" data-id="element-25">
                "Como y me inflamo… y ya no me siento cómoda en mi propio
                cuerpo."
              </p>
            </div>
            <p data-id="element-26">
              Si tienes 65+ y te pasa esto, no necesitas una dieta nueva.
              Necesitas un protocolo pequeño que puedas repetir sin pelearte con
              tu vida.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6" style={{
      backgroundColor: COLORS.cream
    }} data-id="element-27">
        <div className="max-w-5xl mx-auto" data-id="element-28">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={fadeIn} className="text-center mb-10 sm:mb-16" data-id="element-29">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#3D6B6B] mb-3 sm:mb-4" data-id="element-30">
              Esto es lo que probablemente ya intentaste...
            </h2>
            <p className="text-lg sm:text-xl text-gray-600" data-id="element-31">
              Y por qué sientes que nada funciona a largo plazo.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6" data-id="element-32">
            {[{
            icon: Leaf,
            title: 'Tés y Detox',
            desc: 'Demasiado agresivos o solo un parche temporal.'
          }, {
            icon: Utensils,
            title: 'Comer Menos',
            desc: 'Te deja con hambre y sigues igual de incómoda.'
          }, {
            icon: AlertCircle,
            title: 'Quitar Todo',
            desc: 'Vivir limitada y con miedo a comer no es vida.'
          }, {
            icon: Activity,
            title: 'Videos Sueltos',
            desc: 'Sin un sistema claro, es imposible ser constante.'
          }].map((item, i) => <motion.div key={i} variants={fadeIn} className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-sm" data-id="element-33">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#3D6B6B]/10 flex items-center justify-center mb-3 sm:mb-6 text-[#3D6B6B]" data-id="element-34">
                  <item.icon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-35" />
                </div>
                <h3 className="font-bold text-sm sm:text-lg mb-2 sm:mb-3 text-[#3D6B6B]" data-id="element-36">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-xs sm:text-sm" data-id="element-37">{item.desc}</p>
              </motion.div>)}
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mt-8 sm:mt-12 max-w-2xl mx-auto" data-id="element-38">
            <p className="text-lg sm:text-xl font-medium text-[#3D6B6B]" data-id="element-39">
              No es porque te falte fuerza de voluntad. <br data-id="element-40" />
              Es porque te falta{' '}
              <span className="underline decoration-[#E8846B] decoration-2 sm:decoration-4 underline-offset-4" data-id="element-41">
                un sistema
              </span>
              .
            </p>
          </motion.div>
        </div>
      </section>

      {/* SOLUTION REVEAL */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 bg-[#3D6B6B] text-white overflow-hidden relative" data-id="element-42">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none" data-id="element-43">
          <svg width="100%" height="100%" data-id="element-44">
            <pattern id="pattern-circles" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-45">
              <circle cx="2" cy="2" r="1" fill="currentColor" data-id="element-46" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#pattern-circles)" data-id="element-47" />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-48">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={fadeIn} data-id="element-49">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-4 sm:mb-8" data-id="element-50">
              La idea central <br data-id="element-51" />
              <span className="text-[#F5F0E8] opacity-90 text-lg sm:text-2xl lg:text-3xl font-normal" data-id="element-52">
                (y por qué por fin se siente posible)
              </span>
            </h2>
            <p className="text-base sm:text-xl lg:text-2xl leading-relaxed opacity-90 mb-8 sm:mb-12" data-id="element-53">
              La hinchazón cambia rápido. No necesitas convertir tu vida en un
              proyecto. Solo necesitas activar 3 palancas pequeñas, en el orden
              correcto.
            </p>
            <div className="inline-block border-2 border-[#E8846B] rounded-full px-6 sm:px-8 py-2 sm:py-3 text-lg sm:text-xl font-bold tracking-wide bg-[#E8846B]/10 backdrop-blur-sm" data-id="element-54">
              RESET 21
            </div>
          </motion.div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-white -mt-6 sm:-mt-10 rounded-t-[2rem] sm:rounded-t-[3rem] relative z-20" data-id="element-55">
        <div className="max-w-6xl mx-auto" data-id="element-56">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8" data-id="element-57">
            {/* Pillar 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: 0.1
          }} className="bg-[#F5F0E8] p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl relative overflow-hidden" data-id="element-58">
              <div className="absolute top-0 right-0 bg-[#E8846B] text-white font-bold py-1 px-3 sm:px-4 rounded-bl-xl text-xs sm:text-sm" data-id="element-59">
                PASO 1
              </div>
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-sm text-[#3D6B6B]" data-id="element-60">
                <Clock className="w-6 h-6 sm:w-8 sm:h-8" data-id="element-61" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#3D6B6B] mb-3 sm:mb-4" data-id="element-62">
                CAMINATA 12
              </h3>
              <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base" data-id="element-63">
                <strong data-id="element-64">12 minutos después de comer</strong>, a ritmo
                conversacional.
              </p>
              <p className="text-xs sm:text-sm text-gray-600" data-id="element-65">
                No para "quemar", sino para ayudar a tu cuerpo a procesar sin
                quedarse "atorado".
              </p>
            </motion.div>

            {/* Pillar 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: 0.2
          }} className="bg-[#F5F0E8] p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl relative overflow-hidden" data-id="element-66">
              <div className="absolute top-0 right-0 bg-[#E8846B] text-white font-bold py-1 px-3 sm:px-4 rounded-bl-xl text-xs sm:text-sm" data-id="element-67">
                PASO 2
              </div>
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-sm text-[#3D6B6B]" data-id="element-68">
                <Utensils className="w-6 h-6 sm:w-8 sm:h-8" data-id="element-69" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#3D6B6B] mb-3 sm:mb-4" data-id="element-70">
                PLATO SIMPLE
              </h3>
              <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base" data-id="element-71">
                <strong data-id="element-72">3 intercambios simples</strong> que reducen volumen.
              </p>
              <p className="text-xs sm:text-sm text-gray-600" data-id="element-73">
                Más cocido amable, menos crudo pesado. Sin contar calorías, solo
                combinando mejor.
              </p>
            </motion.div>

            {/* Pillar 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: 0.3
          }} className="bg-[#F5F0E8] p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl relative overflow-hidden sm:col-span-2 md:col-span-1" data-id="element-74">
              <div className="absolute top-0 right-0 bg-[#E8846B] text-white font-bold py-1 px-3 sm:px-4 rounded-bl-xl text-xs sm:text-sm" data-id="element-75">
                PASO 3
              </div>
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-sm text-[#3D6B6B]" data-id="element-76">
                <Armchair className="w-6 h-6 sm:w-8 sm:h-8" data-id="element-77" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#3D6B6B] mb-3 sm:mb-4" data-id="element-78">
                SILLA-PARED 6
              </h3>
              <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base" data-id="element-79">
                <strong data-id="element-80">6 movimientos suaves</strong>, sin ir al piso.
              </p>
              <p className="text-xs sm:text-sm text-gray-600" data-id="element-81">
                Para postura, circulación y activación suave. Sin dolor y sin
                miedo a lesionarte.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-white" data-id="element-82">
        <div className="max-w-5xl mx-auto" data-id="element-83">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12" data-id="element-84">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeIn} className="bg-[#F0FDF4] p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl border border-green-100" data-id="element-85">
              <h3 className="text-xl sm:text-2xl font-bold text-green-800 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3" data-id="element-86">
                <Check className="w-5 h-5 sm:w-6 sm:h-6 bg-green-200 rounded-full p-0.5 sm:p-1" data-id="element-87" />
                Para ti si...
              </h3>
              <ul className="space-y-3 sm:space-y-4" data-id="element-88">
                {['Tienes 65+ y quieres sentirte cómoda en tu ropa otra vez', 'Te inflamas después de comer o al final del día', 'No quieres dietas extremas ni pasar hambre', 'No quieres rutinas en el piso', 'Quieres algo simple que puedas hacer en casa'].map((item, i) => <li key={i} className="flex items-start gap-2 sm:gap-3 text-green-900 text-sm sm:text-base" data-id="element-89">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-90" />
                    <span data-id="element-91">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeIn} className="bg-[#FEF2F2] p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl border border-red-100" data-id="element-92">
              <h3 className="text-xl sm:text-2xl font-bold text-red-800 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3" data-id="element-93">
                <X className="w-5 h-5 sm:w-6 sm:h-6 bg-red-200 rounded-full p-0.5 sm:p-1" data-id="element-94" />
                No es para ti si...
              </h3>
              <ul className="space-y-3 sm:space-y-4" data-id="element-95">
                {["Buscas una promesa de 'bajar X libras en X días'", 'Quieres entrenamientos de alta intensidad', 'Tienes síntomas severos que requieren médico', "Buscas una 'pastilla mágica' sin hacer cambios", 'Prefieres contar calorías estrictamente'].map((item, i) => <li key={i} className="flex items-start gap-2 sm:gap-3 text-red-900 text-sm sm:text-base" data-id="element-96">
                    <X className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-97" />
                    <span data-id="element-98">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6" style={{
      backgroundColor: COLORS.cream
    }} data-id="element-99">
        <div className="max-w-5xl mx-auto" data-id="element-100">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={fadeIn} className="text-center mb-8 sm:mb-12" data-id="element-101">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#3D6B6B] mb-3 sm:mb-4" data-id="element-102">
              Todo lo que recibes hoy
            </h2>
            <p className="text-lg sm:text-xl text-gray-600" data-id="element-103">
              Un sistema completo, digital y listo para usar.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center" data-id="element-104">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} className="bg-white p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl shadow-lg order-2 lg:order-1" data-id="element-105">
              <ul className="space-y-4 sm:space-y-6" data-id="element-106">
                {[{
                title: 'Plan completo RESET 21',
                desc: 'La guía paso a paso de 21 días.'
              }, {
                title: 'Guía CAMINATA 12',
                desc: 'Con versión para hacer dentro de casa.'
              }, {
                title: 'PLATO SIMPLE',
                desc: '3 swaps inteligentes sin ingredientes raros.'
              }, {
                title: 'Rutina SILLA-PARED 6',
                desc: 'Con técnica segura y semáforo de dolor.'
              }, {
                title: "Checklist de 'gatillos'",
                desc: 'Identifica qué te inflama sin eliminar todo.'
              }, {
                title: 'Protocolo para comer fuera',
                desc: 'Disfruta socialmente sin miedo.'
              }, {
                title: 'Hojas imprimibles',
                desc: 'Tracker semanal + calendario 21 días.'
              }, {
                title: 'Guía de emergencia',
                desc: 'Qué hacer cuando ya estás inflamada.'
              }].map((item, i) => <li key={i} className="flex items-start gap-3 sm:gap-4" data-id="element-107">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#E8846B]/20 flex items-center justify-center flex-shrink-0 text-[#E8846B]" data-id="element-108">
                      <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-109" />
                    </div>
                    <div data-id="element-110">
                      <h4 className="font-bold text-[#3D6B6B] text-sm sm:text-base" data-id="element-111">
                        {item.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600" data-id="element-112">
                        {item.desc}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} className="relative order-1 lg:order-2 max-w-xs sm:max-w-sm mx-auto lg:max-w-none" data-id="element-113">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl" data-id="element-114">
                <img src="https://cdn.magicpatterns.com/uploads/bhNzPPaBTBr2BY1JxcdAt6/LIBRO_NUEVO.png" alt="Vista previa del contenido" className="w-full h-auto" data-id="element-115" />
              </div>
              <div className="hidden sm:block absolute -bottom-6 -right-6 w-32 h-32 bg-[#E8846B] rounded-full opacity-20 blur-2xl" data-id="element-116"></div>
              <div className="hidden sm:block absolute -top-6 -left-6 w-32 h-32 bg-[#3D6B6B] rounded-full opacity-20 blur-2xl" data-id="element-117"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* OFFER SECTION */}
      <section id="offer" className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 bg-white" data-id="element-118">
        <div className="max-w-4xl mx-auto" data-id="element-119">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={fadeIn} className="bg-[#3D6B6B] rounded-2xl sm:rounded-[2.5rem] p-6 sm:p-8 lg:p-16 text-center text-white relative overflow-hidden shadow-xl sm:shadow-2xl" data-id="element-120">
            {/* Background decoration - hidden on mobile */}
            <div className="hidden sm:block absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none" data-id="element-121">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" data-id="element-122"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#E8846B] rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" data-id="element-123"></div>
            </div>

            <div className="relative z-10" data-id="element-124">
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-4 sm:mb-6" data-id="element-125">
                Recupera tu comodidad diaria
              </h2>
              <p className="text-base sm:text-xl opacity-90 mb-6 sm:mb-8 max-w-2xl mx-auto" data-id="element-126">
                Una compra pequeña para un cambio grande. Sin mensualidades,
                pago único.
              </p>

              <div className="flex items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-8" data-id="element-127">
                <span className="text-5xl sm:text-6xl font-bold tracking-tight" data-id="element-128">
                  $6.99
                </span>
              </div>

              <a href="https://cuerpoenpaz-2.myshopify.com/cart/47421589422267:1" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 rounded-xl text-lg sm:text-2xl font-bold text-white shadow-xl active:scale-95 sm:hover:shadow-2xl sm:hover:-translate-y-1 transition-all duration-300 mb-6 sm:mb-8 bg-[#E8846B] inline-block text-center" data-id="element-129">
                Descargar RESET 21 Ahora
              </a>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 max-w-lg mx-auto border border-white/20" data-id="element-130">
                <h4 className="font-bold flex items-center justify-center gap-2 mb-2 text-sm sm:text-base" data-id="element-131">
                  <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-132" /> Garantía de
                  Satisfacción
                </h4>
                <p className="text-xs sm:text-sm opacity-90" data-id="element-133">
                  Si lo compras, lo lees y sientes que no es para ti, te
                  devuelvo el dinero sin preguntas. Tienes 30 días para
                  probarlo.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <footer className="py-12 sm:py-16 px-4 sm:px-6 text-center" style={{
      backgroundColor: COLORS.cream
    }} data-id="element-134">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="max-w-2xl mx-auto" data-id="element-135">
          <h3 className="text-xl sm:text-2xl font-bold text-[#3D6B6B] mb-4 sm:mb-6" data-id="element-136">
            Empieza hoy con la única acción que no falla: <br data-id="element-137" />
            <span className="text-[#E8846B]" data-id="element-138">CAMINATA 12</span> después de tu
            próxima comida.
          </h3>
          <button onClick={scrollToOffer} className="inline-flex items-center gap-2 font-bold text-[#3D6B6B] active:text-[#E8846B] sm:hover:text-[#E8846B] transition-colors text-base sm:text-lg" data-id="element-139">
            Obtener el protocolo completo{' '}
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-140" />
          </button>

          <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-gray-200 text-xs sm:text-sm text-gray-500" data-id="element-141">
            <p data-id="element-142">
              © {new Date().getFullYear()} RESET 21. Todos los derechos
              reservados.
            </p>
            <p className="mt-2 text-xs" data-id="element-143">
              Este producto no sustituye el consejo médico profesional.
            </p>
          </div>
        </motion.div>
      </footer>
    </div>;
}